import { useQuery, useQueryClient } from "react-query";
import { supabase } from "../../../config/supabaseClient";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { getAllDatesFromSelection } from "../../StayUpToDate/RenderComponent";

// Initialize dayjs plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export function useLoadPostIdsDataYesterday(
  username: any,
  user_id: any,
  postIds: any[] = [],
  numberOfDays: any[] = [],
  selectedDays?: any[],
  componentType?: string,
  order?: string,
  componentId?: string
) {
  const queryClient = useQueryClient();

  const getPostIdsFeed = async () => {
    let finalData: any = [];
    
    // Calculate date range based on numberOfDays or selectedDays
    let numDayStart = "";
    let numDayEnd = "";

    if (componentType === "daywise" && selectedDays) {
      const updatedDates = getAllDatesFromSelection(selectedDays);
      if (updatedDates.length === 1) {
        numDayStart = dayjs(updatedDates[0])
          .utc()
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
        numDayEnd = dayjs(updatedDates[0])
          .utc()
          .endOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
      } else {
        numDayStart = dayjs(
          Math.min(...updatedDates.map((date) => dayjs(date).utc().valueOf()))
        )
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
        numDayEnd = dayjs(
          Math.max(...updatedDates.map((date) => dayjs(date).utc().valueOf()))
        )
          .endOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
      }
    } else if (numberOfDays.length > 0) {
      const num = numberOfDays[0];
      if (num === 30) {
        numDayStart = dayjs()
          .utc()
          .subtract(1, "month")
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
        numDayEnd = dayjs()
          .utc()
          .endOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
      } else if (num === 7) {
        numDayStart = dayjs()
          .utc()
          .subtract(num, "day")
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
        numDayEnd = dayjs()
          .utc()
          .endOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
      } else {
        // For yesterday (num === 1) or any other case
        numDayStart = dayjs()
          .utc()
          .subtract(num, "day")
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
        numDayEnd = dayjs()
          .utc()
          .subtract(num, "day")
          .endOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
      }
    } else {
      // Default to yesterday if no numberOfDays or componentType specified
      numDayStart = dayjs()
        .utc()
        .subtract(1, "day")
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
      numDayEnd = dayjs()
        .utc()
        .subtract(1, "day")
        .endOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
    }

    // Only proceed with query if we have valid dates and postIds
    if (!postIds?.length) {
      return [];
    }

    // First, get all posts that are in removed_postids
    const { data: allRemovedPosts = [], error: removedPostsError }: any = await supabase
      .from(username)
      .select("post_id, post_date")
      .in("post_id", postIds)
      .ilike("index", "%active%");

    if (removedPostsError) {
      console.error("Error fetching removed posts:", removedPostsError);
      return [];
    }

    // Filter out posts that are no longer in the time range
    const postsInTimeRange = allRemovedPosts.filter((post: any) => {
      const postDate = dayjs(post.post_date);
      return postDate.isSameOrAfter(numDayStart) && postDate.isSameOrBefore(numDayEnd);
    });

    // Get post_ids that are no longer in the time range
    const validPostIds = postsInTimeRange.map((post: any) => post.post_id.toString());
    const outdatedPostIds = postIds.filter(id => !validPostIds.includes(id.toString()));

    // If there are outdated post_ids, update the component
    if (outdatedPostIds.length > 0 && componentId) {
      const currentRemovedPostIds = postIds.filter(id => validPostIds.includes(id.toString()));
      
      // Update the stayuptodate_components table
      const { error: updateError } = await supabase
        .from("stayuptodate_components")
        .update({
          removed_postids: currentRemovedPostIds.join(",")
        })
        .eq("id", componentId);

      if (updateError) {
        console.error("Error updating removed_postids:", updateError);
      }
    }

    // Continue with the original query but only for posts in time range
    if (validPostIds.length > 0) {
      const { data: postIdData = [], error: postIdError }: any = await supabase
        .from(username)
        .select("*")
        .in("post_id", validPostIds)
        .ilike("index", "%active%");

      if (postIdError) {
        console.error("Error fetching post data:", postIdError);
        return [];
      }

      finalData = postIdData;
    }

    // Sort based on postIds order while keeping only posts within date range
    const sortedPostData = postIds
      .map((postId) => finalData.find((data: any) => data.post_id.toString() === postId))
      .filter(Boolean); // Remove undefined entries

    return sortedPostData;
  };

  const { data, error, isLoading } = useQuery(
    ["postIdsFeed", user_id, ...postIds, numberOfDays, selectedDays, componentType],
    getPostIdsFeed,
    {
      enabled: Boolean(postIds && postIds.length >= 0),
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    }
  );

  return {
    data,
    error,
    isLoading,
    refetch: () =>
      queryClient.invalidateQueries(["postIdsFeed", user_id, ...postIds]),
  };
}

